import React, { useCallback, useState } from 'react';
import { Upload, Button, Image, Tag, Typography, Progress, Space, Tooltip } from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';

const { Dragger } = Upload;
const { Text } = Typography;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_MB = 10;
const MAX_FILES = 15;

export interface LocalImage {
  uid: string;
  file: File;
  previewUrl: string;
  name: string;
  size: number;
  valid: boolean;
  error?: string;
}

interface ImageUploaderProps {
  images: LocalImage[];
  onChange: (images: LocalImage[]) => void;
  uploadProgress?: number;
  uploading?: boolean;
  maxFiles?: number;
  disabled?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onChange,
  uploadProgress,
  uploading = false,
  maxFiles = MAX_FILES,
  disabled = false,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: `Unsupported format. Use JPEG, PNG, WebP, or GIF.` };
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return { valid: false, error: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${MAX_SIZE_MB} MB.` };
    }
    return { valid: true };
  };

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const remaining = maxFiles - images.length;
      const toAdd = newFiles.slice(0, remaining);

      const newImages: LocalImage[] = toAdd.map(file => {
        const validation = validateFile(file);
        return {
          uid: `${Date.now()}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          ...validation,
        };
      });

      onChange([...images, ...newImages]);
    },
    [images, maxFiles, onChange],
  );

  const removeImage = (uid: string) => {
    const img = images.find(i => i.uid === uid);
    if (img) URL.revokeObjectURL(img.previewUrl);
    onChange(images.filter(i => i.uid !== uid));
  };

  const preview = (src: string) => {
    setPreviewSrc(src);
    setPreviewVisible(true);
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]): boolean => {
    // Only process the full list once (on last file)
    if (file === fileList[fileList.length - 1]) {
      addFiles(fileList as unknown as File[]);
    }
    return false; // prevent auto-upload
  };

  const validCount = images.filter(i => i.valid).length;
  const invalidCount = images.filter(i => !i.valid).length;

  return (
    <div>
      {/* Drop zone */}
      {images.length < maxFiles && !disabled && (
        <Dragger
          multiple
          accept=".jpg,.jpeg,.png,.webp,.gif"
          beforeUpload={beforeUpload}
          showUploadList={false}
          disabled={disabled || uploading}
          style={{ marginBottom: 16, borderRadius: 8 }}
        >
          <p className="ant-upload-drag-icon">
            {uploading ? (
              <LoadingOutlined style={{ fontSize: 40, color: '#fe5a1d' }} />
            ) : (
              <InboxOutlined style={{ fontSize: 40, color: '#fe5a1d' }} />
            )}
          </p>
          <p className="ant-upload-text" style={{ fontWeight: 600 }}>
            Drag & drop images here, or click to select
          </p>
          <p className="ant-upload-hint" style={{ color: '#888' }}>
            JPEG, PNG, WebP, GIF · Max {MAX_SIZE_MB} MB per image · Up to {maxFiles} images total
          </p>
          <p className="ant-upload-hint">
            {images.length} / {maxFiles} added
          </p>
        </Dragger>
      )}

      {/* Upload progress */}
      {uploading && uploadProgress !== undefined && (
        <div style={{ marginBottom: 12 }}>
          <Progress percent={uploadProgress} strokeColor="#fe5a1d" size="small" />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Uploading and processing images…
          </Text>
        </div>
      )}

      {/* Summary */}
      {images.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Tag color="green">{validCount} valid</Tag>
            {invalidCount > 0 && <Tag color="red">{invalidCount} invalid</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              First image will be the cover photo
            </Text>
          </Space>
        </div>
      )}

      {/* Image grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: 10,
        }}
      >
        {images.map((img, idx) => (
          <div
            key={img.uid}
            style={{
              position: 'relative',
              borderRadius: 8,
              overflow: 'hidden',
              border: img.valid ? '2px solid #e8e8e8' : '2px solid #ff4d4f',
              background: '#fafafa',
            }}
          >
            {/* Cover badge */}
            {idx === 0 && img.valid && (
              <div
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  background: '#fe5a1d',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 4,
                  zIndex: 2,
                  letterSpacing: '0.5px',
                }}
              >
                COVER
              </div>
            )}

            {/* Image */}
            <div
              style={{
                height: 90,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={img.previewUrl}
                alt={img.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            {/* Error overlay */}
            {!img.valid && (
              <Tooltip title={img.error}>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,77,79,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'help',
                  }}
                >
                  <Text type="danger" style={{ fontSize: 10, textAlign: 'center', padding: '0 4px' }}>
                    Invalid
                  </Text>
                </div>
              </Tooltip>
            )}

            {/* Filename */}
            <div
              style={{
                padding: '3px 4px',
                fontSize: 10,
                color: '#555',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                background: '#fff',
              }}
              title={img.name}
            >
              {img.name}
            </div>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '3px 4px',
                background: '#fff',
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Tooltip title="Preview">
                <Button
                  size="small"
                  type="text"
                  icon={<EyeOutlined style={{ fontSize: 12 }} />}
                  onClick={() => preview(img.previewUrl)}
                  style={{ padding: '0 4px', height: 20 }}
                />
              </Tooltip>
              <Tooltip title="Remove">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                  onClick={() => removeImage(img.uid)}
                  disabled={disabled}
                  style={{ padding: '0 4px', height: 20 }}
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </div>

      {/* Hidden Ant Design preview */}
      <Image
        src={previewSrc}
        style={{ display: 'none' }}
        preview={{
          visible: previewVisible,
          src: previewSrc,
          onVisibleChange: v => setPreviewVisible(v),
        }}
      />
    </div>
  );
};

export default ImageUploader;
